/**
 * SuggestionReviewPane — steward's expanded review surface (Block 6.C.2;
 * Block 6.G.1 B1 adds success-toast feedback on Approve + Reject).
 *
 * Side-by-side old/new + rationale + Approve/Reject buttons. Reject
 * expands an inline review_note textarea before commit.
 *
 * Per ADR-017 F1 (no modal); F7 (no "Are you sure?" — disposition IS the
 * gate). Both Approve and Reject fire dispatchEmail to suggester
 * server-side as part of the same trx.
 *
 * Block 6.G.1 B1 — success-toast feedback. Pre-6.G.1 the success path
 * had no positive feedback signal: row vanished from the Pending list
 * after invalidation, but to an operator that read as ambiguous (did
 * the click work? did nothing happen?). Approve had implicit
 * confirmation via the resulting change_log row appearing on the
 * detail page; Reject had nothing — it just made the row disappear.
 * 6.G.1 adds toast text post-success for both actions (sage uniform
 * per design plan r2 §2.2). Pattern mirrors RecentActivityTab.tsx's
 * archive/restore toast (auto-clear via useEffect after 4 seconds).
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/lib/auth';
import { canApprove } from '@/lib/capabilities';
import type { SuggestedEditWithJoinDisplay } from '@/lib/types';
import { suggestedEditsApi } from '@/lib/suggestedEdits';
import { suggestionRefusalText } from '@/lib/suggestionRefusals';
import { useSupportContact } from '@/hooks/useSupportContact';
import { invalidateAdminReviewKeys } from '@/lib/adminReview';
import { humanizeFieldName, humanizeFieldValue } from '@/lib/fieldDisplay';
import { useJunctionLabelResolver } from '@/lib/useJunctionLabels';
import { refreshFamily } from '@/lib/refreshFamily';

const TOAST_AUTO_CLEAR_MS = 4000;

export interface SuggestionReviewPaneProps {
  tenantSlug: string;
  suggestion: SuggestedEditWithJoinDisplay;
  invalidationKey: ReadonlyArray<unknown>;
}

export function SuggestionReviewPane({
  tenantSlug,
  suggestion,
  invalidationKey,
}: SuggestionReviewPaneProps): ReactNode {
  const queryClient = useQueryClient();
  const { whoami } = useTenant();
  // The Approve/Reject affordances mirror their endpoints' gate ('approve'),
  // NOT the queue's read gate (read_all_suggestions) — a read-only
  // read_all_suggestions holder (board_observer) sees the diff but no
  // disposition buttons (they would always 403).
  const userCanApprove = canApprove(whoami);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const contact = useSupportContact();
  // Block 6.G.1 B1 — toast surfaces the success path. Sage uniform per
  // design plan r2 §2.2 (sage = positive operator outcome; warm-orange
  // reserved for destructive-intent confirmations per 6.F.5 archive
  // pattern).
  const [toast, setToast] = useState<string | null>(null);

  // Auto-clear toast after 4 seconds; cancel on unmount or re-toast.
  useEffect(() => {
    if (toast === null) return;
    const handle = setTimeout(() => setToast(null), TOAST_AUTO_CLEAR_MS);
    return () => clearTimeout(handle);
  }, [toast]);

  // The suggestion, the note and the list to refresh (every status tab:
  // the family) are read at the press and travel as the variables.
  const approveMutation = useMutation({
    mutationFn: async (v: { id: string; family: readonly unknown[] }) =>
      suggestedEditsApi.approve(tenantSlug, v.id, { review_note: null }),
    onSuccess: async (_res, v) => {
      // Block 6.G.1 B1 — set toast BEFORE invalidation so the user sees
      // positive feedback immediately; the refetch races below.
      setToast('Suggestion approved. Change applied.');
      await queryClient.invalidateQueries({ queryKey: v.family });
      // Block 6.F.5 §7.8: approve emits change_log + flips suggested_edits
      // → invalidate admin-review timeline + badge-count.
      await invalidateAdminReviewKeys(queryClient);
    },
    onError: (e: Error) => setError(suggestionRefusalText(e, contact)),
  });

  const rejectMutation = useMutation({
    mutationFn: async (v: { id: string; note: string | null; family: readonly unknown[] }) =>
      suggestedEditsApi.reject(tenantSlug, v.id, { review_note: v.note }),
    onSuccess: async (_res, v) => {
      // Block 6.G.1 B1 — toast IS the operator's feedback signal for
      // Reject (no timeline footprint per design plan r2 §2.4; reject
      // does not emit a change_log row, only flips suggested_edits.status).
      setToast('Suggestion rejected.');
      await queryClient.invalidateQueries({ queryKey: v.family });
      // Block 6.F.5 §7.8: reject flips suggested_edits.status (pending
      // count drops) → invalidate admin-review timeline + badge-count.
      await invalidateAdminReviewKeys(queryClient);
    },
    onError: (e: Error) => setError(suggestionRefusalText(e, contact)),
  });

  const isPending = approveMutation.isPending || rejectMutation.isPending;

  // Item 5 — resolve junction slugs to display names for the diff render.
  const isJunction =
    suggestion.target_type === 'organization' &&
    (suggestion.field_name === 'subcategories' ||
      suggestion.field_name === 'specialties');
  const labelFor = useJunctionLabelResolver(tenantSlug, isJunction);
  const valueOpts = {
    targetType: suggestion.target_type,
    fieldName: suggestion.field_name,
    labelFor,
  };

  return (
    <div className="px-3 py-3 border-t border-sage-800/[8%]">
      {/* Block 6.G.1 B1 — success toast (sage uniform per design plan r2 §2.2).
          Auto-clears after 4s via the useEffect above. */}
      {toast && (
        <div
          role="status"
          className="mb-3 px-3 py-2 text-sm bg-sage-50 border border-sage-800/[8%] text-sage-700 rounded-md"
        >
          {toast}
        </div>
      )}

      <div className="text-xs text-ink-soft mb-1">Field</div>
      <div className="text-sm font-medium text-sage-800 mb-3">
        {humanizeFieldName(suggestion.target_type, suggestion.field_name)}
      </div>

      <div className="text-xs text-ink-soft mb-1">Currently</div>
      <div className="text-sm bg-sand text-ink px-2 py-1 mb-3 rounded-[10px]">
        {humanizeFieldValue(suggestion.old_value, valueOpts)}
      </div>

      <div className="text-xs text-ink-soft mb-1">Suggested</div>
      <div className="text-sm bg-sage-50 text-ink px-2 py-1 mb-3 rounded-[10px]">
        {humanizeFieldValue(suggestion.new_value, valueOpts)}
      </div>

      {suggestion.rationale && (
        <>
          <div className="text-xs text-ink-soft mb-1">Why?</div>
          <div className="text-sm italic mb-3 text-ink-soft">"{suggestion.rationale}"</div>
        </>
      )}

      <div className="text-xs text-ink-soft mb-3">
        Suggested by {suggestion.suggester_display} · {formatTimestamp(suggestion.created_at)}
      </div>

      {error && (
        <div role="alert" className="text-xs text-red-700 mb-2" data-testid="suggestion-review-error">
          ⚠ {error}
        </div>
      )}

      {!rejectMode && userCanApprove && (
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={() => setRejectMode(true)}
            disabled={isPending}
            className="min-h-11 px-4 py-1 text-sm text-warm-700 border border-warm-300 rounded-pill hover:bg-warm-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
          >
            Reject
          </button>
          <button
            type="button"
            onClick={() => approveMutation.mutate({ id: suggestion.id, family: refreshFamily(invalidationKey) })}
            disabled={isPending}
            className="min-h-11 px-4 py-1 text-sm font-semibold bg-sage-800 text-cream rounded-pill shadow-sh1 hover:bg-forest-700 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
          >
            {approveMutation.isPending ? 'Approving…' : 'Approve'}
          </button>
        </div>
      )}

      {rejectMode && (
        <div className="border border-warm-300 bg-warm-50 rounded-md p-3 mt-2">
          <label className="block text-xs text-ink-soft mb-1" htmlFor={`reject-note-${suggestion.id}`}>
            Reviewer note (optional)
          </label>
          <textarea
            id={`reject-note-${suggestion.id}`}
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            rows={3}
            aria-label="Reviewer note"
            disabled={isPending}
            className="w-full min-h-11 px-3 py-2 mb-2 border border-sage-800/[8%] rounded-md text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
          />
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => {
                setRejectMode(false);
                setRejectNote('');
              }}
              disabled={isPending}
              className="min-h-11 px-3 py-1 text-sm text-ink-soft hover:text-sage-800"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() =>
                rejectMutation.mutate({
                  id: suggestion.id,
                  note: rejectNote.trim().length === 0 ? null : rejectNote.trim(),
                  family: refreshFamily(invalidationKey),
                })
              }
              disabled={isPending}
              className="min-h-11 px-4 py-1 text-sm font-semibold bg-warm-600 text-cream rounded-pill shadow-sh1 hover:bg-warm-700 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
            >
              {rejectMutation.isPending ? 'Rejecting…' : 'Confirm reject'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function formatTimestamp(iso: string | null): string {
  if (iso === null) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const ms = Date.now() - d.getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
